<template>
    <VJoyWrapper class="portfolio-filled-state" direction="column" row-gap="4" no-margin>
        <VJoyWrapper class="portfolio-filled-state__list" direction="row" justify="flex-start" align="stretch" column-gap="4" no-margin>
            <PortfolioItemCard
                v-for="item in displayedItems"
                :key="item.id"
                :portfolio-item="item"
                :is-edition-mode="isEditionMode"
                :can-share="$ff('portfolio-share-item')"
                @click="openPortfolioItem(item.id)"
                @share="sharePortfolioItem(item)"
                @edit="emit('edit', item.id)"
                @move-to-drafts="emit('move-to-drafts', item.id)"
                @delete="emit('delete', item.id)"
            />
        </VJoyWrapper>
        <PortfolioShareDialog ref="shareDialogRef" :share-url="shareUrl" :portfolio-item-id="shareItemId" />
        <ShowMoreByItemCount
            :total="portfolioItems.length"
            :limit="collapsedLimit"
            :label="showMoreLabel"
            test-id="portfolio-show-more"
            :locked="{
                allowHref: true,
                componentName: 'profile-portfolio-section',
                targetId: anchorsPageProfile.portfolioSection,
                label: showMoreLabel,
            }"
            @expand="isExpanded = true"
            @collapse="isExpanded = false"
        />
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {navigateTo, useRoute, useTranslation} from '#imports';
    import {useMediaQuery} from '@malt/utils';
    import {VJoyWrapper} from '@maltjoy/core-vue';
    import type {PortfolioItemSummaryResource} from '@api';
    import ShowMoreByItemCount from '~/components/_common/show-more/ShowMoreByItemCount.vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {anchorsPageProfile} from '~/config/anchors';
    import PortfolioItemCard from '~/features/portfolio/section/ui/PortfolioItemCard.vue';
    import PortfolioShareDialog from '~/features/portfolio/section/ui/PortfolioShareDialog.vue';

    const props = defineProps<{
        portfolioItems: PortfolioItemSummaryResource[];
        isEditionMode?: boolean;
    }>();

    const emit = defineEmits<{
        edit: [itemId: string];
        'move-to-drafts': [itemId: string];
        delete: [itemId: string];
    }>();

    const {t} = useTranslation();
    const route = useRoute();
    const isExpanded = ref(false);
    const {trackPortfolioItemClicked} = useProfileTracking();
    const shareDialogRef = ref<InstanceType<typeof PortfolioShareDialog>>();
    const shareUrl = ref('');
    const shareItemId = ref('');

    async function openPortfolioItem(itemId: string) {
        trackPortfolioItemClicked(itemId, 'profile_page');
        await navigateTo({
            path: route.path,
            query: route.query,
            hash: `#${anchorsPageProfile.portfolioItemById(itemId)}`,
        });
    }

    function sharePortfolioItem(item: PortfolioItemSummaryResource) {
        shareUrl.value = `${window.location.origin}${route.path}#${anchorsPageProfile.portfolioItemById(item.id)}`;
        shareItemId.value = item.id;
        shareDialogRef.value?.show();
    }

    const {matchNavbarMedia} = useMediaQuery();
    const collapsedLimit = computed(() => {
        if (matchNavbarMedia.value.min.Md) return 3;
        if (matchNavbarMedia.value.min.Xs) return 2;
        return 1;
    });
    const displayedItems = computed(() => (isExpanded.value ? props.portfolioItems : props.portfolioItems.slice(0, collapsedLimit.value)));
    const showMoreLabel = computed(() =>
        t('profile.section.portfolio.show.more.items.label', {
            count: props.portfolioItems.length,
        }),
    );
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint' as joy;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .portfolio-filled-state {
        &__list {
            width: 100%;
            display: grid;
            grid-template-columns: repeat(v-bind(collapsedLimit), 304px);
            gap: var(--joy-core-spacing-4);
            justify-content: start;

            @include mq.screen_xs() {
                grid-template-columns: repeat(1, min(404px, 70vw));
                justify-content: center;
            }
        }
    }
</style>
